import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Target } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const AnalyticsDashboard = ({ location, timeframe, category }) => {
  const metrics = [
    {
      id: 'total-hazards',
      label: 'Total Hazards',
      value: 324,
      change: +12,
      changePercent: '+3.8%',
      trend: 'up',
      icon: AlertTriangle,
      color: 'blue',
      description: 'Identified hazards',
    },
    {
      id: 'critical-risks',
      label: 'Critical Risks',
      value: 18,
      change: -3,
      changePercent: '-14.3%',
      trend: 'down',
      icon: AlertTriangle,
      color: 'red',
      description: 'Requiring immediate action',
    },
    {
      id: 'mitigated',
      label: 'Mitigated Hazards',
      value: 267,
      change: +23,
      changePercent: '+9.4%',
      trend: 'up',
      icon: CheckCircle,
      color: 'green',
      description: 'Successfully controlled',
    },
    {
      id: 'pending-review',
      label: 'Pending Review',
      value: 39,
      change: -8,
      changePercent: '-17.0%',
      trend: 'down',
      icon: Clock,
      color: 'yellow',
      description: 'Awaiting assessment',
    },
    {
      id: 'compliance-rate',
      label: 'Compliance Rate',
      value: '94.2%',
      change: +2.1,
      changePercent: '+2.3%',
      trend: 'up',
      icon: Target,
      color: 'purple',
      description: 'Overall compliance',
    },
    {
      id: 'avg-resolution',
      label: 'Avg Resolution Time',
      value: '5.2 days',
      change: -0.8,
      changePercent: '-13.3%',
      trend: 'down',
      icon: Clock,
      color: 'indigo',
      description: 'Time to resolution',
    },
  ];

  const getColorClasses = (color, trend) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-900',
        icon: 'text-blue-600',
        light: 'text-blue-600',
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-900',
        icon: 'text-red-600',
        light: 'text-red-600',
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-900',
        icon: 'text-green-600',
        light: 'text-green-600',
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-900',
        icon: 'text-yellow-600',
        light: 'text-yellow-600',
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-900',
        icon: 'text-purple-600',
        light: 'text-purple-600',
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-900',
        icon: 'text-indigo-600',
        light: 'text-indigo-600',
      },
    };
    return colors?.[color] || colors?.blue;
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Key Risk Indicators</h2>
        <p className="text-sm text-gray-600 mt-1">
          Period-over-period comparison and trending analysis
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics?.map((metric) => {
          const Icon = metric?.icon;
          const colors = getColorClasses(metric?.color, metric?.trend);
          const TrendIcon = metric?.trend === 'up' ? TrendingUp : TrendingDown;
          const trendColor = metric?.trend === 'up' 
            ? (metric?.id === 'critical-risks' ? 'text-red-600' : 'text-green-600')
            : (metric?.id === 'critical-risks' ? 'text-green-600' : 'text-red-600');

          return (
            <div
              key={metric?.id}
              className={`${colors?.bg} border ${colors?.border} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 ${colors?.bg} rounded-lg`}>
                  <Icon className={`w-6 h-6 ${colors?.icon}`} />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${trendColor}`}>
                  <TrendIcon className="w-4 h-4" />
                  {metric?.changePercent}
                </div>
              </div>
              <div>
                <div className={`text-3xl font-bold ${colors?.text} mb-1`}>
                  {metric?.value}
                </div>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  {metric?.label}
                </div>
                <div className="text-xs text-gray-600">
                  {metric?.description}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">vs previous period</span>
                  <span className={`font-medium ${trendColor}`}>
                    {metric?.change > 0 ? '+' : ''}{metric?.change}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;